import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  Building,
  CalendarDays,
  CalendarRange,
  Globe,
  Sparkles,
  Sun,
  Users,
  type LucideIcon,
} from 'lucide-react'
import {
  displayPersonName,
  formatRelativeTime,
  landingPageLabel,
  useAnalyticsOrgs,
  useAnalyticsOverview,
  useAnalyticsPages,
  useAnalyticsUsers,
  type AnalyticsOrgRow,
  type AnalyticsPageRow,
  type AnalyticsPages,
  type AnalyticsUserRow,
} from 'src/api/db/apiAnalytics'
import { paths } from 'src/routes/paths'
import { AnalyticsActivityChart } from 'src/shared/components/AnalyticsActivityChart'
import { PageContainer } from 'src/shared/components/PageContainer'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Skeleton } from 'src/shared/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/shared/components/ui/tabs'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

type AnalyticsPeriod = 1 | 7 | 30

const PERIODS: Array<{
  days: AnalyticsPeriod
  labelEn: string
  labelSv: string
  icon: LucideIcon
  tone: {
    wash: string
    icon: string
    selected: string
  }
}> = [
  {
    days: 1,
    labelEn: 'Day',
    labelSv: 'Dag',
    icon: Sun,
    tone: {
      wash: 'from-amber-400/20',
      icon: 'bg-amber-100 text-amber-800',
      selected:
        'border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50/80 to-background',
    },
  },
  {
    days: 7,
    labelEn: 'Week',
    labelSv: 'Vecka',
    icon: CalendarDays,
    tone: {
      wash: 'from-sky-400/18',
      icon: 'bg-sky-100 text-sky-700',
      selected:
        'border-sky-300 bg-gradient-to-br from-sky-50 via-blue-50/70 to-background',
    },
  },
  {
    days: 30,
    labelEn: 'Month',
    labelSv: 'Månad',
    icon: CalendarRange,
    tone: {
      wash: 'from-violet-400/18',
      icon: 'bg-violet-100 text-violet-700',
      selected:
        'border-violet-300 bg-gradient-to-br from-violet-50 via-fuchsia-50/60 to-background',
    },
  },
]

const formatDailyRate = (value: number) =>
  value >= 10 ? String(Math.round(value)) : value.toFixed(1).replace(/\.0$/, '')

const monthlyVisitorPrediction = (daily: AnalyticsPages['daily']) => {
  const todayKey = new Date().toLocaleDateString('sv-SE')
  const completeDays = daily
    .filter((row) => String(row.day).slice(0, 10) < todayKey)
    .slice(-7)
  if (completeDays.length === 0) return null

  const dailyVisitors =
    completeDays.reduce((sum, row) => sum + row.unique_visitors, 0) /
    completeDays.length
  const dailyViews =
    completeDays.reduce((sum, row) => sum + row.views, 0) / completeDays.length

  return {
    visitors: Math.round(dailyVisitors * 30),
    views: Math.round(dailyViews * 30),
    dailyVisitors,
    limited: completeDays.length < 3,
  }
}

const Kpi = ({
  label,
  value,
  hint,
}: {
  label: string
  value: string | number
  hint?: string
}) => (
  <Column className="min-w-0 gap-0.5 rounded-xl border border-[hsl(var(--border-subtle))] bg-background p-4">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-2xl font-semibold tabular-nums">{value}</p>
    {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </Column>
)

const PeriodCard = ({
  label,
  views,
  visitors,
  viewsLabel,
  visitorsLabel,
  selected,
  icon: Icon,
  tone,
  onClick,
}: {
  label: string
  views: number
  visitors: number
  viewsLabel: string
  visitorsLabel: string
  selected: boolean
  icon: LucideIcon
  tone: {
    wash: string
    icon: string
    selected: string
  }
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'card-new group relative min-w-0 overflow-hidden p-4 text-left',
      'transition-transform duration-200 hover:-translate-y-0.5',
      selected ? tone.selected : 'bg-background'
    )}
  >
    <div
      className={cn(
        'pointer-events-none absolute inset-0 bg-gradient-to-br via-transparent to-transparent transition-opacity duration-200',
        tone.wash,
        selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      )}
    />
    <Column className="relative gap-3">
      <Row className="items-center gap-2">
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
            tone.icon
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={2.2} />
        </div>
        <p className="text-sm font-medium">{label}</p>
      </Row>
      <Row className="gap-4">
        <Column className="min-w-0 gap-0.5">
          <p className="text-2xl font-semibold tabular-nums">{views}</p>
          <p className="text-xs text-muted-foreground">{viewsLabel}</p>
        </Column>
        <Column className="min-w-0 gap-0.5">
          <p className="text-2xl font-semibold tabular-nums">{visitors}</p>
          <p className="text-xs text-muted-foreground">{visitorsLabel}</p>
        </Column>
      </Row>
    </Column>
  </button>
)

const PredictionCard = ({
  label,
  value,
  hint,
}: {
  label: string
  value: string | number
  hint: string
}) => (
  <Column className="card-new relative min-w-0 gap-3 overflow-hidden bg-gradient-to-br from-emerald-50 via-lime-50 to-teal-50 p-4">
    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
    <Row className="items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-400 to-teal-600 text-white shadow-[0_4px_12px_-2px_rgba(16,185,129,0.7)]">
        <Sparkles className="h-4 w-4" strokeWidth={2.2} />
      </div>
      <p className="text-sm font-medium">{label}</p>
    </Row>
    <p className="bg-gradient-to-r from-emerald-600 via-green-500 to-teal-600 bg-clip-text text-2xl font-semibold tabular-nums text-transparent">
      {value}
    </p>
    <p className="text-xs text-muted-foreground">{hint}</p>
  </Column>
)

const Section = ({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}) => (
  <Column className="gap-4">
    <Column className="gap-0.5">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Column>
    {children}
  </Column>
)

const ChartPanel = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <Column className="gap-3 rounded-xl border border-[hsl(var(--border-subtle))] p-4">
    <p className="text-sm font-medium">{title}</p>
    {children}
  </Column>
)

type OrgSortKey = keyof Pick<
  AnalyticsOrgRow,
  | 'name'
  | 'event_count'
  | 'active_users'
  | 'logins'
  | 'share_opens'
  | 'request_created'
  | 'chat_sent'
  | 'last_active_at'
>

type UserSortKey = keyof Pick<
  AnalyticsUserRow,
  | 'event_count'
  | 'logins'
  | 'request_created'
  | 'chat_sent'
  | 'last_active_at'
  | 'last_login_at'
  | 'org_name'
  | 'email'
>

type PageSortKey = keyof Pick<
  AnalyticsPageRow,
  'path' | 'views' | 'unique_visitors' | 'last_viewed_at'
>

const compareValues = (
  a: string | number | null,
  b: string | number | null,
  desc: boolean
) => {
  const empty = desc ? -1 : 1
  if (a == null && b == null) return 0
  if (a == null) return empty
  if (b == null) return -empty
  if (typeof a === 'number' && typeof b === 'number') {
    return desc ? b - a : a - b
  }
  const result = String(a).localeCompare(String(b), undefined, {
    sensitivity: 'base',
  })
  return desc ? -result : result
}

const SortHeader = ({
  label,
  active,
  desc,
  onClick,
}: {
  label: string
  active: boolean
  desc: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    className="inline-flex items-center gap-1 text-left"
    onClick={onClick}
  >
    {label}
    {active ? (
      desc ? (
        <ArrowDown className="h-3.5 w-3.5" />
      ) : (
        <ArrowUp className="h-3.5 w-3.5" />
      )
    ) : null}
  </button>
)

export const AdminAnalyticsPage = () => {
  const { t, locale } = useLocalization()
  const navigate = useNavigate()
  const lang = locale === 'sv' ? 'sv' : 'en'
  const [period, setPeriod] = useState<AnalyticsPeriod>(30)
  const [detailTab, setDetailTab] = useState('organizations')
  const [orgSort, setOrgSort] = useState<{ key: OrgSortKey; desc: boolean }>({
    key: 'event_count',
    desc: true,
  })
  const [userSort, setUserSort] = useState<{ key: UserSortKey; desc: boolean }>(
    {
      key: 'event_count',
      desc: true,
    }
  )
  const [pageSort, setPageSort] = useState<{ key: PageSortKey; desc: boolean }>({
    key: 'views',
    desc: true,
  })

  const overview = useAnalyticsOverview(period)
  const orgs = useAnalyticsOrgs(period)
  const users = useAnalyticsUsers(period)
  const pagesDay = useAnalyticsPages(1)
  const pagesWeek = useAnalyticsPages(7)
  const pagesMonth = useAnalyticsPages(30)
  const pagesByPeriod = {
    1: pagesDay,
    7: pagesWeek,
    30: pagesMonth,
  } as const
  const selectedPages = pagesByPeriod[period]
  const landingPrediction = monthlyVisitorPrediction(
    pagesMonth.data?.daily ?? []
  )
  const isLoading =
    (overview.isPending && !overview.data) ||
    (orgs.isPending && !orgs.data) ||
    (users.isPending && !users.data)
  const landingError =
    pagesDay.isError || pagesWeek.isError || pagesMonth.isError

  const sortedOrgs = [...(orgs.data ?? [])].sort((a, b) =>
    compareValues(a[orgSort.key], b[orgSort.key], orgSort.desc)
  )

  const sortedUsers = [...(users.data ?? [])].sort((a, b) => {
    if (userSort.key === 'email') {
      const nameA = displayPersonName({
        firstName: a.first_name,
        lastName: a.last_name,
        email: a.email,
        fallback: '',
      })
      const nameB = displayPersonName({
        firstName: b.first_name,
        lastName: b.last_name,
        email: b.email,
        fallback: '',
      })
      return compareValues(nameA, nameB, userSort.desc)
    }
    return compareValues(a[userSort.key], b[userSort.key], userSort.desc)
  })

  const sortedPages = [...(selectedPages.data?.pages ?? [])].sort((a, b) => {
    if (pageSort.key === 'path') {
      return compareValues(
        landingPageLabel(a.path, t),
        landingPageLabel(b.path, t),
        pageSort.desc
      )
    }
    return compareValues(a[pageSort.key], b[pageSort.key], pageSort.desc)
  })

  const toggleOrgSort = (key: OrgSortKey) => {
    setOrgSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: true }
    )
  }

  const toggleUserSort = (key: UserSortKey) => {
    setUserSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: true }
    )
  }

  const togglePageSort = (key: PageSortKey) => {
    setPageSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: true }
    )
  }

  return (
    <PageContainer className="gap-10">
      <Column className="gap-4">
        <Column className="min-w-0 gap-1">
          <Row className="items-center gap-2">
            <BarChart3 className="h-6 w-6 shrink-0" />
            <h1>{t('Analytics', 'Analys')}</h1>
          </Row>
          <p className="text-sm text-muted-foreground">
            {t(
              'Traffic, new organizations, and request activity. Choose a period to update the whole page.',
              'Trafik, nya organisationer och förfrågningsaktivitet. Välj en period för att uppdatera hela sidan.'
            )}
          </p>
        </Column>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {PERIODS.map((item) => {
            const periodPages = pagesByPeriod[item.days].data
            return (
              <PeriodCard
                key={item.days}
                label={t(item.labelEn, item.labelSv)}
                views={periodPages?.page_views ?? 0}
                visitors={periodPages?.unique_visitors ?? 0}
                viewsLabel={t('Page views', 'Sidvisningar')}
                visitorsLabel={t('Unique visitors', 'Unika besökare')}
                selected={period === item.days}
                icon={item.icon}
                tone={item.tone}
                onClick={() => setPeriod(item.days)}
              />
            )
          })}
          <PredictionCard
            label={t(
              'Predicted monthly visitors',
              'Prognos månadsbesökare'
            )}
            value={landingPrediction?.visitors ?? '—'}
            hint={
              landingPrediction
                ? landingPrediction.limited
                  ? t(
                      `Limited data (${formatDailyRate(landingPrediction.dailyVisitors)}/day). ~${landingPrediction.views} page views`,
                      `Begränsad data (${formatDailyRate(landingPrediction.dailyVisitors)}/dag). ~${landingPrediction.views} sidvisningar`
                    )
                  : t(
                      `Based on last 7 days (${formatDailyRate(landingPrediction.dailyVisitors)}/day). ~${landingPrediction.views} page views`,
                      `Baserat på senaste 7 dagarna (${formatDailyRate(landingPrediction.dailyVisitors)}/dag). ~${landingPrediction.views} sidvisningar`
                    )
                : t('Not enough data', 'Inte tillräckligt med data')
            }
          />
        </div>
      </Column>

      {isLoading ? (
        <Column className="gap-3">
          <Row className="gap-3">
            <Skeleton className="h-20 flex-1" />
            <Skeleton className="h-20 flex-1" />
            <Skeleton className="h-20 flex-1" />
            <Skeleton className="h-20 flex-1" />
          </Row>
          <Skeleton className="h-56 w-full" />
          <Skeleton className="h-56 w-full" />
        </Column>
      ) : overview.isError ? (
        <p className="text-sm text-muted-foreground">
          {t(
            'Could not load analytics.',
            'Kunde inte ladda analys.'
          )}
        </p>
      ) : (
        <Column className="gap-10">
          <Section
            title={t('Reach', 'Räckvidd')}
            description={t(
              'Who is finding the site, and how that pace projects to a month.',
              'Vem som hittar sajten, och vad den takten innebär för en månad.'
            )}
          >
            <ChartPanel title={t('Visits over time', 'Besök över tid')}>
              {landingError ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    'Could not load landing page visits.',
                    'Kunde inte ladda landningsbesök.'
                  )}
                </p>
              ) : (
                <AnalyticsActivityChart
                  locale={locale}
                  stacked={false}
                  data={(selectedPages.data?.daily ?? []).map((row) => ({
                    day: row.day,
                    views: row.views,
                    unique_visitors: row.unique_visitors,
                  }))}
                  series={[
                    {
                      key: 'views',
                      label: t('Page views', 'Sidvisningar'),
                      color: '#fb923c',
                    },
                    {
                      key: 'unique_visitors',
                      label: t('Unique visitors', 'Unika besökare'),
                      color: '#38bdf8',
                    },
                  ]}
                />
              )}
            </ChartPanel>
          </Section>

          <Section
            title={t('Growth', 'Tillväxt')}
            description={t(
              'Organizations we added, who signed up, and who stayed active.',
              'Organisationer vi lade till, som registrerade sig och som var aktiva.'
            )}
          >
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Kpi
                label={t('Added by us', 'Tillagda av oss')}
                value={overview.data?.org_added ?? 0}
              />
              <Kpi
                label={t('Self-serve signups', 'Självregistreringar')}
                value={overview.data?.org_signup ?? 0}
              />
              <Kpi
                label={t('Active organizations', 'Aktiva organisationer')}
                value={overview.data?.active_orgs ?? 0}
              />
              <Kpi
                label={t('Active users', 'Aktiva användare')}
                value={overview.data?.active_users ?? 0}
              />
            </div>
            <ChartPanel title={t('New organizations', 'Nya organisationer')}>
              <AnalyticsActivityChart
                locale={locale}
                data={(overview.data?.daily ?? []).map((row) => ({
                  day: row.day,
                  org_added: row.org_added,
                  org_signup: row.org_signup,
                }))}
                series={[
                  {
                    key: 'org_added',
                    label: t('Added by us', 'Tillagda av oss'),
                    color: '#818cf8',
                  },
                  {
                    key: 'org_signup',
                    label: t('Self-serve signups', 'Självregistreringar'),
                    color: '#34d399',
                  },
                ]}
              />
            </ChartPanel>
          </Section>

          <Section
            title={t('Demand', 'Efterfrågan')}
            description={t(
              'Requests, conversation, and buying intent in the selected period.',
              'Förfrågningar, konversation och köpintention under den valda perioden.'
            )}
          >
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              <Kpi
                label={t('Requests created', 'Skapade förfrågningar')}
                value={overview.data?.request_created ?? 0}
              />
              <Kpi
                label={t('Messages sent', 'Skickade meddelanden')}
                value={overview.data?.chat_sent ?? 0}
              />
              <Kpi
                label={t('Catalog opens', 'Katalogöppningar')}
                value={overview.data?.catalog_open ?? 0}
              />
              <Kpi
                label={t('Checkouts opened', 'Kassor öppnade')}
                value={overview.data?.checkout_open ?? 0}
              />
              <Kpi
                label={t('Logins', 'Inloggningar')}
                value={overview.data?.logins ?? 0}
              />
              <Kpi
                label={t('Share opens', 'Delöppningar')}
                value={overview.data?.share_opens ?? 0}
              />
            </div>
            <ChartPanel title={t('Activity over time', 'Aktivitet över tid')}>
              <AnalyticsActivityChart
                locale={locale}
                data={(overview.data?.daily ?? []).map((row) => ({
                  day: row.day,
                  request_created: row.request_created,
                  chat_sent: row.chat_sent,
                  logins: row.logins,
                  share_opens: row.share_opens,
                }))}
                series={[
                  {
                    key: 'request_created',
                    label: t('Requests created', 'Skapade förfrågningar'),
                    color: '#fb923c',
                  },
                  {
                    key: 'chat_sent',
                    label: t('Messages sent', 'Skickade meddelanden'),
                    color: '#34d399',
                  },
                  {
                    key: 'logins',
                    label: t('Logins', 'Inloggningar'),
                    color: '#818cf8',
                  },
                  {
                    key: 'share_opens',
                    label: t('Share opens', 'Delöppningar'),
                    color: '#38bdf8',
                  },
                ]}
              />
            </ChartPanel>
          </Section>

          <Section
            title={t('Details', 'Detaljer')}
            description={t(
              'Who was active, and which landing pages they used.',
              'Vilka som var aktiva, och vilka landningssidor de använde.'
            )}
          >
          <Tabs value={detailTab} onValueChange={setDetailTab}>
            <TabsList>
              <TabsTrigger value="organizations" className="gap-1.5">
                <Building className="h-3.5 w-3.5" />
                {t('Organizations', 'Organisationer')}
              </TabsTrigger>
              <TabsTrigger value="users" className="gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {t('Users', 'Användare')}
              </TabsTrigger>
              <TabsTrigger value="landing" className="gap-1.5">
                <Globe className="h-3.5 w-3.5" />
                {t('Landing pages', 'Landningssidor')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="organizations" className="mt-4">
              {sortedOrgs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    'No organization activity in this period.',
                    'Ingen organisationsaktivitet under den här perioden.'
                  )}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <SortHeader
                          label={t('Organization', 'Organisation')}
                          active={orgSort.key === 'name'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('name')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Activity', 'Aktivitet')}
                          active={orgSort.key === 'event_count'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('event_count')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Active users', 'Aktiva användare')}
                          active={orgSort.key === 'active_users'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('active_users')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Requests', 'Förfrågningar')}
                          active={orgSort.key === 'request_created'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('request_created')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Messages', 'Meddelanden')}
                          active={orgSort.key === 'chat_sent'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('chat_sent')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Logins', 'Inloggningar')}
                          active={orgSort.key === 'logins'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('logins')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Share opens', 'Delöppningar')}
                          active={orgSort.key === 'share_opens'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('share_opens')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Last active', 'Senast aktiv')}
                          active={orgSort.key === 'last_active_at'}
                          desc={orgSort.desc}
                          onClick={() => toggleOrgSort('last_active_at')}
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedOrgs.map((org) => (
                      <TableRow
                        key={org.id}
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(`${paths.adminOrganizations}/${org.id}`)
                        }
                      >
                        <TableCell className="font-medium">
                          {org.name || t('Untitled org', 'Namnlös org')}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {org.event_count}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {org.active_users}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {org.request_created ?? 0}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {org.chat_sent ?? 0}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {org.logins}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {org.share_opens}
                        </TableCell>
                        <TableCell>
                          {formatRelativeTime(org.last_active_at, lang)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="users" className="mt-4">
              {users.isError ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    'Could not load user activity.',
                    'Kunde inte ladda användaraktivitet.'
                  )}
                </p>
              ) : sortedUsers.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    'No user activity in this period.',
                    'Ingen användaraktivitet under den här perioden.'
                  )}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <SortHeader
                          label={t('User', 'Användare')}
                          active={userSort.key === 'email'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('email')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Organization', 'Organisation')}
                          active={userSort.key === 'org_name'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('org_name')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Activity', 'Aktivitet')}
                          active={userSort.key === 'event_count'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('event_count')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Requests', 'Förfrågningar')}
                          active={userSort.key === 'request_created'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('request_created')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Messages', 'Meddelanden')}
                          active={userSort.key === 'chat_sent'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('chat_sent')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Logins', 'Inloggningar')}
                          active={userSort.key === 'logins'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('logins')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Last login', 'Senaste inloggning')}
                          active={userSort.key === 'last_login_at'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('last_login_at')}
                        />
                      </TableHead>
                      <TableHead>
                        <SortHeader
                          label={t('Last active', 'Senast aktiv')}
                          active={userSort.key === 'last_active_at'}
                          desc={userSort.desc}
                          onClick={() => toggleUserSort('last_active_at')}
                        />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.map((user) => (
                      <TableRow
                        key={`${user.id}-${user.org_id ?? 'none'}`}
                        className={user.org_id ? 'cursor-pointer' : undefined}
                        onClick={() => {
                          if (user.org_id) {
                            navigate(
                              `${paths.adminOrganizations}/${user.org_id}`
                            )
                          }
                        }}
                      >
                        <TableCell>
                          <Column className="gap-0.5">
                            <span className="font-medium">
                              {displayPersonName({
                                firstName: user.first_name,
                                lastName: user.last_name,
                                email: user.email,
                                fallback: t('Unknown user', 'Okänd användare'),
                              })}
                            </span>
                            {user.email &&
                            (user.first_name || user.last_name) ? (
                              <span className="text-xs text-muted-foreground">
                                {user.email}
                              </span>
                            ) : null}
                          </Column>
                        </TableCell>
                        <TableCell>
                          {user.org_name || t('No organization', 'Ingen organisation')}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {user.event_count}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {user.request_created ?? 0}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {user.chat_sent ?? 0}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {user.logins}
                        </TableCell>
                        <TableCell>
                          {formatRelativeTime(user.last_login_at, lang)}
                        </TableCell>
                        <TableCell>
                          {formatRelativeTime(user.last_active_at, lang)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="landing" className="mt-4">
              {landingError ? (
                <p className="text-sm text-muted-foreground">
                  {t(
                    'Could not load landing page visits.',
                    'Kunde inte ladda landningsbesök.'
                  )}
                </p>
              ) : sortedPages.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t(
                        'No landing page visits in this period.',
                        'Inga landningsbesök under den här perioden.'
                      )}
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            <SortHeader
                              label={t('Page', 'Sida')}
                              active={pageSort.key === 'path'}
                              desc={pageSort.desc}
                              onClick={() => togglePageSort('path')}
                            />
                          </TableHead>
                          <TableHead>
                            <SortHeader
                              label={t('Views', 'Visningar')}
                              active={pageSort.key === 'views'}
                              desc={pageSort.desc}
                              onClick={() => togglePageSort('views')}
                            />
                          </TableHead>
                          <TableHead>
                            <SortHeader
                              label={t('Unique visitors', 'Unika besökare')}
                              active={pageSort.key === 'unique_visitors'}
                              desc={pageSort.desc}
                              onClick={() => togglePageSort('unique_visitors')}
                            />
                          </TableHead>
                          <TableHead>
                            <SortHeader
                              label={t('Last visit', 'Senaste besök')}
                              active={pageSort.key === 'last_viewed_at'}
                              desc={pageSort.desc}
                              onClick={() => togglePageSort('last_viewed_at')}
                            />
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedPages.map((page) => (
                          <TableRow key={page.path}>
                            <TableCell>
                              <Column className="gap-0.5">
                                <span className="font-medium">
                                  {landingPageLabel(page.path, t)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {page.path}
                                </span>
                              </Column>
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {page.views}
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {page.unique_visitors}
                            </TableCell>
                            <TableCell>
                              {formatRelativeTime(page.last_viewed_at, lang)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
            </TabsContent>
          </Tabs>
          </Section>
        </Column>
      )}
    </PageContainer>
  )
}
